import { Search, UserPlus } from "lucide-react";
import { useMemo, useState } from "react";

import { useTableSort } from "@/components/sortable-table-head";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { type AdminUserSummary, useAdminUsers } from "@/lib/api";

import { InviteUserDialog } from "./invite-user-dialog";
import { UserDeleteDialog } from "./user-delete-dialog";
import { UserDetailDialog } from "./user-detail-dialog";
import { UserEditDialog } from "./user-edit-dialog";
import { UserTable } from "./user-table";

export function AdminUsersPage() {
  const { sortBy, sortDir, onSort } = useTableSort();
  const { data: users = [], isLoading: loading } = useAdminUsers({
    sortBy: sortBy ?? undefined,
    sortDir,
  });
  const [search, setSearch] = useState("");

  // Dialog state
  const [viewUser, setViewUser] = useState<AdminUserSummary | null>(null);
  const [editUser, setEditUser] = useState<AdminUserSummary | null>(null);
  const [deleteUser, setDeleteUser] = useState<AdminUserSummary | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!search.trim()) {
      return users;
    }
    const q = search.toLowerCase();
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        (u.full_name ?? "").toLowerCase().includes(q) ||
        (u.first_name ?? "").toLowerCase().includes(q) ||
        (u.last_name ?? "").toLowerCase().includes(q),
    );
  }, [users, search]);

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-primary text-2xl font-normal">Users</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Manage your team &mdash; {users.length} member{users.length !== 1 ? "s" : ""}
          </p>
        </div>
        <Button onClick={() => setInviteOpen(true)}>
          <UserPlus className="mr-2 size-4" />
          Invite
        </Button>
      </div>

      {/* Table card */}
      <div className="bg-card border-border mt-6 overflow-hidden rounded-lg border">
        {/* Search bar inside card */}
        <div className="border-border border-b px-4 py-3">
          <div className="relative max-w-sm">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-muted-foreground py-16 text-center text-sm">Loading users...</div>
        ) : (
          <UserTable
            users={filtered}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={onSort}
            onView={setViewUser}
            onEdit={setEditUser}
            onDelete={setDeleteUser}
          />
        )}
      </div>

      {/* Dialogs */}
      <UserDetailDialog
        user={viewUser}
        open={viewUser !== null}
        onOpenChange={(open) => {
          if (!open) {
            setViewUser(null);
          }
        }}
      />

      <UserEditDialog
        user={editUser}
        open={editUser !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditUser(null);
          }
        }}
      />

      <UserDeleteDialog
        user={deleteUser}
        open={deleteUser !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteUser(null);
          }
        }}
      />

      <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  );
}
